// src/utils/scheduleGeneration.js
// Pure schedule-generation logic. Extracted from App.js and parameterized
// on volunteersPerShift and allowBackToBack.

function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

function isVolunteerInSlot(volunteer, slotAssignments) {
  // slotAssignments is array-of-arrays (multi-location) or flat array (single)
  if (!slotAssignments) return false;
  if (Array.isArray(slotAssignments[0])) {
    return slotAssignments.some(loc => loc.includes(volunteer));
  }
  return slotAssignments.includes(volunteer);
}

function isVolunteerAvailable(volunteer, slotIndex, assignedVolunteers, allowBackToBack) {
  if (allowBackToBack) return true;
  if (slotIndex === 0) return true;
  const previous = assignedVolunteers[slotIndex - 1];
  if (!previous) return true;
  return !isVolunteerInSlot(volunteer, previous);
}

// Caller guarantees volunteer IDs don't contain '|' (the app uses generated IDs like "volunteer-0").
function pairKey(a, b) {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

// Pick the next volunteer to add to a partially-filled slot.
// Score = (sum of pair counts vs. already-assigned in this slot) * 100 + shiftCount.
// Lower is better. Preserves the original "pair variety" optimization for the
// 2-per-shift case and degrades gracefully for N>2.
function pickNextVolunteer(candidates, alreadyInSlot, shiftCounts, pairCounts, randomize) {
  if (candidates.length === 0) return null;
  let pool = randomize ? shuffleArray(candidates) : candidates;
  let best = pool[0];
  let bestScore = Infinity;
  for (const v of pool) {
    const pairSum = alreadyInSlot.reduce((acc, other) => acc + (pairCounts[pairKey(v, other)] || 0), 0);
    const score = pairSum * 100 + shiftCounts[v];
    if (score < bestScore) {
      bestScore = score;
      best = v;
    }
  }
  return best;
}

function bumpPairCounts(slotMembers, pairCounts) {
  for (let i = 0; i < slotMembers.length; i++) {
    for (let j = i + 1; j < slotMembers.length; j++) {
      const k = pairKey(slotMembers[i], slotMembers[j]);
      pairCounts[k] = (pairCounts[k] || 0) + 1;
    }
  }
}

function createSingleLocationSchedule(volunteers, slots, options) {
  const { randomize, volunteersPerShift, allowBackToBack } = options;
  const filtered = volunteers.filter(v => v && String(v).trim() !== '');

  const shiftCounts = {};
  filtered.forEach(v => { shiftCounts[v] = 0; });
  const pairCounts = {};

  const assignments = slots.map(() => []);

  // Per-slot target count: slot override (if any) wins over global.
  const targetFor = (slotIndex) => {
    const override = slots[slotIndex] && slots[slotIndex].volunteerCount;
    return typeof override === 'number' ? override : volunteersPerShift;
  };

  // Pass 1: respect back-to-back if required; optimize for pair variety + workload
  slots.forEach((_, slotIndex) => {
    const target = targetFor(slotIndex);
    while (assignments[slotIndex].length < target) {
      const candidates = filtered.filter(v =>
        !assignments[slotIndex].includes(v) &&
        isVolunteerAvailable(v, slotIndex, assignments, allowBackToBack)
      );
      const chosen = pickNextVolunteer(
        candidates, assignments[slotIndex], shiftCounts, pairCounts, randomize
      );
      if (chosen === null) break;
      assignments[slotIndex].push(chosen);
      shiftCounts[chosen]++;
    }
    bumpPairCounts(assignments[slotIndex], pairCounts);
  });

  // Pass 2: fill any remaining spots, relaxing back-to-back
  slots.forEach((_, slotIndex) => {
    const target = targetFor(slotIndex);
    const before = assignments[slotIndex].length;
    while (assignments[slotIndex].length < target) {
      const candidates = filtered.filter(v => !assignments[slotIndex].includes(v));
      const chosen = pickNextVolunteer(
        candidates, assignments[slotIndex], shiftCounts, pairCounts, randomize
      );
      if (chosen === null) break;
      assignments[slotIndex].push(chosen);
      shiftCounts[chosen]++;
    }
    if (assignments[slotIndex].length > before) {
      // Bump pairs among the newly-added volunteers only. Cross-pairs between
      // Pass-1 and Pass-2 members are intentionally skipped — pair counts are
      // a heuristic, not a correctness invariant, and recomputing them here
      // would require tracking which originals are "already paired."
      bumpPairCounts(assignments[slotIndex].slice(before), pairCounts);
    }
  });

  return { assignments, shiftCounts };
}

function createMultiLocationSchedule(volunteers, slots, options) {
  const { randomize, locationCount, volunteersPerShift, allowBackToBack } = options;
  const filtered = volunteers.filter(v => v && String(v).trim() !== '');

  const shiftCounts = {};
  filtered.forEach(v => { shiftCounts[v] = 0; });
  const pairCounts = {};

  const assignments = slots.map(() =>
    Array(locationCount).fill(null).map(() => [])
  );

  // Per (slot, location) target: slot.locationCounts[locIndex] wins over global.
  const targetFor = (slotIndex, locIndex) => {
    const counts = slots[slotIndex] && slots[slotIndex].locationCounts;
    const override = counts && counts[locIndex];
    return typeof override === 'number' ? override : volunteersPerShift;
  };

  // Pass 1
  slots.forEach((_, slotIndex) => {
    for (let locIndex = 0; locIndex < locationCount; locIndex++) {
      const target = targetFor(slotIndex, locIndex);
      while (assignments[slotIndex][locIndex].length < target) {
        const candidates = filtered.filter(v =>
          !isVolunteerInSlot(v, assignments[slotIndex]) &&
          isVolunteerAvailable(v, slotIndex, assignments, allowBackToBack)
        );
        const chosen = pickNextVolunteer(
          candidates, assignments[slotIndex][locIndex], shiftCounts, pairCounts, randomize
        );
        if (chosen === null) break;
        assignments[slotIndex][locIndex].push(chosen);
        shiftCounts[chosen]++;
      }
      bumpPairCounts(assignments[slotIndex][locIndex], pairCounts);
    }
  });

  // Pass 2: fill remaining spots, relaxing back-to-back but keeping
  // "not in this slot's other locations" rule
  slots.forEach((_, slotIndex) => {
    for (let locIndex = 0; locIndex < locationCount; locIndex++) {
      const target = targetFor(slotIndex, locIndex);
      while (assignments[slotIndex][locIndex].length < target) {
        const candidates = filtered.filter(v =>
          !isVolunteerInSlot(v, assignments[slotIndex])
        );
        const chosen = pickNextVolunteer(
          candidates, assignments[slotIndex][locIndex], shiftCounts, pairCounts, randomize
        );
        if (chosen === null) break;
        assignments[slotIndex][locIndex].push(chosen);
        shiftCounts[chosen]++;
      }
    }
  });

  return { assignments, shiftCounts };
}

export function createSchedule(volunteers, slots, options) {
  const opts = {
    randomize: false,
    multipleLocations: false,
    locationCount: 1,
    volunteersPerShift: 2,
    allowBackToBack: false,
    ...options,
  };
  if (opts.multipleLocations) {
    return createMultiLocationSchedule(volunteers, slots, opts);
  }
  return createSingleLocationSchedule(volunteers, slots, opts);
}
